/** Section 3 - hero specialties.
 *
 * A specialty is the one hero bonus that cannot be authored as a flat modifier,
 * because every one of them scales with the hero's level. So content names
 * *what* the hero is specialised in and code owns *how much*: `HeroDef.specialty`
 * carries a `kind` from a closed union and a `target` id, and the five formulas
 * below turn that pair plus a level into ordinary `Modifier`s. The authored
 * `specialty.modifiers` list is still honoured on top, for the handful of
 * specialties that really are a flat number.
 *
 * The formulas, and why they are these numbers
 * --------------------------------------------
 * The classic never published them; these reproduce its observable behaviour and are
 * pinned by `specialty.spec.ts` so a retune is a deliberate act.
 *
 *   creature    5% of the creature's OWN base attack, defense and damage per
 *               `floor(heroLevel / tier)` steps, plus one point of speed. The
 *               divisor is why a Scrapper specialist outgrows a Great Maw one: a
 *               tier-1 creature banks a step every level, a tier-7 one every
 *               seventh. The percentage is of the *creature's* stat, not of the
 *               stack's, so the hero's own Attack is never multiplied twice.
 *   warMachine  the same rule aimed at `warmachine.<target>`, minus the speed
 *               step (a machine that never moves gains nothing from one), and
 *               the machine is wheeled out even without its artifact - a
 *               ballista specialist who has to go shopping for a ballista is
 *               not a specialist.
 *   skill       5% of the skill's own effect per hero level, capped at +100%.
 *               A skill whose effect is a *rule* rather than a number has
 *               nothing to scale, so it counts one level higher instead,
 *               capped at expert. Exactly one of the two branches ever fires.
 *   spell       3% per hero level on that spell's damage and healing, capped
 *               at +150%.
 *   resource    a flat daily yield, authored in `specialty.modifiers` as an
 *               `income.*` add, because it is the one specialty that does not
 *               scale. Handled here only so the kind is not a silent no-op.
 *
 * Nothing in this file names a hero, a creature or a spell.
 */

import type { CreatureDefId, HeroDefId, ModifierSrcId, SkillDefId } from "../ids.js";
import { asCreatureDefId, asModifierSrcId } from "../ids.js";
import type { ContentRegistry, CreatureDef, HeroDef } from "../content/registry.js";
import type { CreatureModifiers, Modifier, StatKey } from "../modifiers.js";
import { FP_ONE, roundHalfAway } from "../numeric.js";
import type { HeroState, SkillLevel } from "../state.js";

// ------------------------------------------------------------------- kinds

export const SPECIALTY_KINDS = Object.freeze(
  ["creature", "skill", "spell", "warMachine", "resource"] as const,
);
export type SpecialtyKind = (typeof SPECIALTY_KINDS)[number];

export function specialtyKindOf(hd: HeroDef): SpecialtyKind | null {
  for (const k of SPECIALTY_KINDS) if (hd.specialty.kind === k) return k;
  return null;
}

// --------------------------------------------------------------- constants

/** Creature and war-machine specialties: 5% of the base stat per step. */
export const CREATURE_STEP_FP = 50;
/** Skill specialties: 5% of the skill's own effect per hero level ... */
export const SKILL_STEP_FP = 50;
/** ... and never more than double it. */
export const SKILL_CAP_FP = FP_ONE;
/** Spell specialties: 3% per hero level ... */
export const SPELL_STEP_FP = 30;
/** ... capped at two and a half times. */
export const SPELL_CAP_FP = 1500;

/** The war-machine creature behind a `warMachine` specialty's target. */
export const WAR_MACHINE_PREFIX = "warmachine.";

// --------------------------------------------------------------- creatures

/**
 * A creature, every upgrade of it, and the base it upgrades from. Perton's
 * Archers become Master Bowyers and stay his; a hero specialised in the upgrade would
 * otherwise lose the bonus by *not* upgrading, which is backwards.
 */
export function creatureFamily(
  content: ContentRegistry, target: CreatureDefId,
): readonly CreatureDefId[] {
  const root = baseFormOf(content, target);
  if (root === null) return [];
  const out: CreatureDefId[] = [];
  for (const id of [...content.creatures.keys()].sort()) {
    if (baseFormOf(content, id) === root) out.push(id);
  }
  return out;
}

/** Walk `upgradeOf` to the bottom. Bounded: the chain is acyclic by validation. */
function baseFormOf(content: ContentRegistry, id: CreatureDefId): CreatureDefId | null {
  let def = content.creatures.get(id);
  if (def === undefined) return null;
  for (let step = 0; def.upgradeOf !== null && step < 8; step++) {
    const prev = content.creatures.get(def.upgradeOf);
    if (prev === undefined) break;
    def = prev;
  }
  return def.id;
}

/** 5% of `base` per step, rounded once (D4). */
const stepShare = (base: number, steps: number): number =>
  roundHalfAway((base * CREATURE_STEP_FP * steps) / FP_ONE);

function creatureMods(
  src: ModifierSrcId, def: CreatureDef, steps: number, speedStep: boolean,
): readonly Modifier[] {
  const out: Modifier[] = [];
  const add = (stat: StatKey, value: number): void => {
    if (value === 0) return;
    out.push({ source: asModifierSrcId(`${src as string}.${stat}`), stat, op: "add", value });
  };
  add("attack", stepShare(def.attack, steps));
  add("defense", stepShare(def.defense, steps));
  add("damageMin", stepShare(def.damageMin, steps));
  add("damageMax", stepShare(def.damageMax, steps));
  // The speed step is the specialty's signature and does not scale: a
  // specialised creature simply moves one faster, from level one.
  if (speedStep && def.speed > 0) add("speed", 1);
  return out;
}

const srcFor = (hd: HeroDefId, what: string): ModifierSrcId =>
  asModifierSrcId(`specialty.${hd as string}.${what}`);

/**
 * The per-creature half of a hero's specialty, expanded over the whole family.
 * `battle/init.ts` matches these against each stack's def when it builds the
 * store; nothing else has to know a specialty exists.
 */
export function creatureBonusesFor(
  content: ContentRegistry, hd: HeroDef, level: number,
): readonly CreatureModifiers[] {
  const kind = specialtyKindOf(hd);
  if (kind !== "creature" && kind !== "warMachine") return [];
  const targetId = kind === "creature"
    ? asCreatureDefId(hd.specialty.target)
    : asCreatureDefId(WAR_MACHINE_PREFIX + hd.specialty.target);
  const family = kind === "creature" ? creatureFamily(content, targetId)
    : content.creatures.has(targetId) ? [targetId] : [];
  const out: CreatureModifiers[] = [];
  for (const id of family) {
    const def = content.creatures.get(id);
    if (def === undefined) continue;
    const tier = def.tier < 1 ? 1 : def.tier;
    const steps = Math.floor(level / tier);
    const mods = creatureMods(srcFor(hd.id, "creature"), def, steps, kind === "creature");
    if (mods.length > 0) out.push({ def: id, mods });
  }
  return out;
}

/**
 * The machine a war-machine specialist brings whether or not he owns the
 * artifact. Empty for every other specialty.
 */
export function specialtyMachines(
  content: ContentRegistry, hd: HeroDef,
): readonly CreatureDefId[] {
  if (specialtyKindOf(hd) !== "warMachine") return [];
  const id = asCreatureDefId(WAR_MACHINE_PREFIX + hd.specialty.target);
  return content.creatures.has(id) ? [id] : [];
}

// ------------------------------------------------------------------ skills

const skillLevelIn = (h: HeroState, def: SkillDefId): SkillLevel | 0 => {
  for (const s of h.secondary) if (s.def === def) return s.level;
  return 0;
};

/**
 * Does this skill's level carry a number? A skill with no modifier at the
 * hero's level is one whose effect is a coded rule, and it is the level bonus
 * branch that fires for it instead.
 */
function skillHasNumbers(
  content: ContentRegistry, def: SkillDefId, level: SkillLevel,
): boolean {
  const sd = content.skills.get(def);
  return (sd?.levels[level - 1]?.length ?? 0) > 0;
}

/**
 * One extra level of a *rule* skill, for the hero who specialises in it.
 * Returns 0 for everyone else, and for a skill whose bonus is a number - that
 * one is amplified instead, by `skillModifiersFor`.
 */
export function skillLevelBonus(
  content: ContentRegistry, hd: HeroDef, h: HeroState, def: SkillDefId,
): number {
  if (specialtyKindOf(hd) !== "skill") return 0;
  if (hd.specialty.target !== (def as string)) return 0;
  const held = skillLevelIn(h, def);
  if (held === 0 || held >= 3) return 0;
  if (skillHasNumbers(content, def, held)) return 0;
  return 1;
}

/** The effective level of a skill, with the hero's specialty folded in. */
export function effectiveSkillLevel(
  content: ContentRegistry, hd: HeroDef | undefined, h: HeroState, def: SkillDefId,
): 0 | 1 | 2 | 3 {
  const held = skillLevelIn(h, def);
  if (held === 0 || hd === undefined) return held;
  const bumped = held + skillLevelBonus(content, hd, h, def);
  return (bumped > 3 ? 3 : bumped) as 0 | 1 | 2 | 3;
}

/**
 * The skill-amplification half: a second copy of the skill's own modifiers,
 * scaled to 5% of their effect per hero level. An `add` is scaled directly; a
 * `mulFp` is scaled by its distance from unity, so amplifying "+10% movement"
 * yields another "+x% movement" rather than another whole multiplication.
 *
 * Read twice: `specialtyModifiers` carries every stat below into `heroModifiers`
 * (movement, income, morale and the rest all resolve through `statFor`), while
 * Offense and Archery name `damageMin`/`damageMax` and reach the battle only
 * through `skillDamageFp` in `execute.ts` - `battleKit` has no reader for those
 * two keys, so without that second read an Offense specialist paid for a bonus
 * nobody ever applied.
 */
export function skillModifiersFor(
  content: ContentRegistry, hd: HeroDef, h: HeroState, level: number,
): readonly Modifier[] {
  if (specialtyKindOf(hd) !== "skill") return [];
  const def = hd.specialty.target as SkillDefId;
  const held = skillLevelIn(h, def);
  if (held === 0 || !skillHasNumbers(content, def, held)) return [];
  const shareFp = Math.min(SKILL_STEP_FP * level, SKILL_CAP_FP);
  if (shareFp <= 0) return [];
  const src = srcFor(hd.id, "skill");
  const out: Modifier[] = [];
  let k = 0;
  for (const m of content.skills.get(def)?.levels[held - 1] ?? []) {
    const source = asModifierSrcId(`${src as string}.${k++}`);
    if (m.op === "add") {
      const value = roundHalfAway((m.value * shareFp) / FP_ONE);
      if (value !== 0) out.push({ source, stat: m.stat, op: "add", value });
    } else if (m.op === "mulFp") {
      const value = FP_ONE + roundHalfAway(((m.value - FP_ONE) * shareFp) / FP_ONE);
      if (value !== FP_ONE) out.push({ source, stat: m.stat, op: "mulFp", value });
    }
    // `set`, `min` and `max` are ceilings and floors, not effects: amplifying
    // one would move a cap rather than a bonus, so they are left alone.
  }
  return out;
}

// ------------------------------------------------------------------ spells

/**
 * The spell specialty, as a fixed-point multiplier keyed by spell id. It rides
 * to the battle kernel through `heroMagicModifiers`, which is also the file
 * that mints the stat key it travels on - one owner for the key, so the two
 * kernels cannot disagree about its spelling.
 */
export function spellSpecialtyFp(
  hd: HeroDef, level: number,
): Readonly<Record<string, number>> {
  if (specialtyKindOf(hd) !== "spell") return {};
  const gain = Math.min(SPELL_STEP_FP * level, SPELL_CAP_FP);
  if (gain <= 0) return {};
  return { [hd.specialty.target]: FP_ONE + gain };
}

// ------------------------------------------------------------------ the sum

/**
 * What a hero's CLASS is worth. Empty for a class the table does not list, and
 * for every hero when the content ships no table at all.
 *
 * A class is not a specialty and the two are kept apart on purpose: a specialty
 * is one hero's, a class is shared by eight of them, and a reader looking at
 * why a Ranger is quiet needs to land on `levels.classPassive` rather than on
 * that particular Ranger's sheet. They are summed in the same place because
 * both are "things true of this hero that are not his skills".
 */
export function classModifiers(
  content: ContentRegistry, hd: HeroDef | undefined,
): readonly Modifier[] {
  if (hd === undefined) return [];
  return content.levels.classPassive?.[hd.heroClass] ?? [];
}

/**
 * Every modifier a hero's specialty puts on the hero itself: the authored flat
 * list, the skill amplification and the spell bonus, and the class passive.
 * Creature bonuses are not here - they belong to a stack, not to a hero, and
 * travel through `creatureBonusesFor`.
 */
export function specialtyModifiers(
  content: ContentRegistry, hd: HeroDef | undefined, h: HeroState,
): readonly Modifier[] {
  if (hd === undefined) return [];
  const out: Modifier[] = [...hd.specialty.modifiers];
  for (const m of skillModifiersFor(content, hd, h, h.level)) out.push(m);
  for (const m of classModifiers(content, hd)) out.push(m);
  return out;
}
